export type DirectControlMode = "human" | "ai" | "shared";

let mode: DirectControlMode = "human";
let gestureActive = false;
let presenceSocket: WebSocket | undefined;
let reconnectTimer: number | undefined;
let heartbeatTimer: number | undefined;
let gestureSafetyTimer: number | undefined;

const PRESENCE_HEARTBEAT_MS = 1_000;
const GESTURE_SAFETY_TIMEOUT_MS = 10_000;

const presenceUrl = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/agent/input-presence`;

const sendPresence = (active: boolean) => {
  if (presenceSocket?.readyState === WebSocket.OPEN) {
    presenceSocket.send(JSON.stringify({ active }));
  }
};

const stopHeartbeat = () => {
  if (heartbeatTimer !== undefined) window.clearInterval(heartbeatTimer);
  heartbeatTimer = undefined;
};

const startHeartbeat = () => {
  if (!gestureActive || heartbeatTimer !== undefined) return;
  heartbeatTimer = window.setInterval(() => sendPresence(true), PRESENCE_HEARTBEAT_MS);
};

const stopGestureSafetyTimer = () => {
  if (gestureSafetyTimer !== undefined) window.clearTimeout(gestureSafetyTimer);
  gestureSafetyTimer = undefined;
};

function setHumanGesture(active: boolean) {
  if (!active) {
    gestureActive = false;
    stopHeartbeat();
    stopGestureSafetyTimer();
    // Send a defensive release even when local state already says false. This
    // repairs a prior release frame that may have been lost in transit.
    sendPresence(false);
    return;
  }

  const wasActive = gestureActive;
  gestureActive = true;
  stopGestureSafetyTimer();
  gestureSafetyTimer = window.setTimeout(
    () => setHumanGesture(false),
    GESTURE_SAFETY_TIMEOUT_MS,
  );
  if (!presenceSocket || presenceSocket.readyState !== WebSocket.OPEN) connectPresence();
  if (!wasActive) sendPresence(true);
  startHeartbeat();
}

const connectPresence = () => {
  if (presenceSocket && presenceSocket.readyState <= WebSocket.OPEN) return;
  presenceSocket = new WebSocket(presenceUrl);
  presenceSocket.onopen = () => {
    sendPresence(gestureActive);
    startHeartbeat();
  };
  presenceSocket.onclose = () => {
    stopHeartbeat();
    presenceSocket = undefined;
    if (!reconnectTimer) reconnectTimer = window.setTimeout(() => {
      reconnectTimer = undefined;
      connectPresence();
    }, 1000);
  };
};

if (typeof window !== "undefined") connectPresence();

export const directControl = {
  getMode: () => mode,
  canHumanControl: () => mode === "human" || mode === "shared",
  setMode: (next: DirectControlMode) => {
    mode = next;
    if (next === "ai") setHumanGesture(false);
    if (typeof document !== "undefined") document.documentElement.dataset.controlMode = next;
  },
  setHumanGesture,
};
